import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Star, Folder, Plus, Share2, Globe, Lock, Trash2, Edit2,
  User, LogOut, Settings, Menu, X, Copy, Check, ExternalLink,
  Heart, MapPin, MoreVertical
} from "lucide-react";

const EMOJI_OPTIONS = ["📋", "🌙", "✈️", "🎉", "💼", "❤️", "🌟", "🍽️", "🍷", "🎂", "🏆", "🌍"];

const Collections = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [sharedCollections, setSharedCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // New collection dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newCollection, setNewCollection] = useState({
    name: "",
    description: "",
    emoji: "📋",
    is_public: false
  });
  const [creating, setCreating] = useState(false);
  
  // Share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const [myRes, sharedRes] = await Promise.all([
        axios.get(`${API}/collections`, { withCredentials: true }),
        axios.get(`${API}/collections/shared`, { withCredentials: true })
      ]);
      setCollections(myRes.data || []);
      setSharedCollections(sharedRes.data || []);
    } catch (error) {
      console.warn("Error loading collections:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const createCollection = async () => {
    if (!newCollection.name.trim()) {
      toast.error("Bitte gib einen Namen ein");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/collections`, newCollection, { withCredentials: true });
      setCollections(prev => [response.data, ...prev]);
      setShowNewDialog(false);
      setNewCollection({ name: "", description: "", emoji: "📋", is_public: false });
      toast.success("Collection erstellt!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const deleteCollection = async (collectionId) => {
    if (!confirm("Collection wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/collections/${collectionId}`, { withCredentials: true });
      setCollections(prev => prev.filter(c => c.collection_id !== collectionId));
      toast.success("Collection gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const copyShareLink = (collection) => {
    const shareUrl = `${window.location.origin}/collections/shared/${collection.share_code}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link kopiert!");
  };

  const shareWithUser = async () => {
    if (!shareEmail.trim() || !selectedCollection) return;
    
    try {
      await axios.post(
        `${API}/collections/${selectedCollection.collection_id}/share`,
        { emails: [shareEmail] },
        { withCredentials: true }
      );
      toast.success(`Mit ${shareEmail} geteilt!`);
      setShareEmail("");
    } catch (error) {
      toast.error("Fehler beim Teilen");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="collections-page">
      {/* Navigation */}
      <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-5 h-5 text-primary fill-primary" />
              <span className="font-semibold gradient-text">Étoile</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/dashboard')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.dashboard')}</button>
              <button onClick={() => navigate('/restaurants')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.restaurants')}</button>
              <button onClick={() => navigate('/collections')} className="menu-item text-sm text-foreground py-2">Collections</button>
              <button onClick={() => navigate('/preferences')} className="menu-item text-sm text-muted-foreground hover:text-foreground py-2">{t('common.preferences')}</button>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="hidden md:flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user?.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-popover border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />{t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />{t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-2">
                <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.dashboard')}</button>
                <button onClick={() => { navigate('/restaurants'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.restaurants')}</button>
                <button onClick={() => { navigate('/collections'); setMobileMenuOpen(false); }} className="text-left py-2 text-foreground">Collections</button>
                <button onClick={() => { navigate('/preferences'); setMobileMenuOpen(false); }} className="text-left py-2 text-muted-foreground">{t('common.preferences')}</button>
                <button onClick={handleLogout} className="text-left py-2 text-destructive">{t('common.logout')}</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Collections</h1>
            <p className="text-muted-foreground">Organisiere deine Restaurants in Listen</p>
          </div>
          
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="new-collection-button">
                <Plus className="w-4 h-4 mr-2" />
                Neue Collection
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle>Neue Collection erstellen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Emoji</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {EMOJI_OPTIONS.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => setNewCollection(prev => ({ ...prev, emoji }))}
                        className={`text-2xl p-2 rounded-lg transition-all ${
                          newCollection.emoji === emoji 
                            ? "bg-primary/20 ring-2 ring-primary" 
                            : "hover:bg-muted/50"
                        }`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Name</Label>
                  <Input
                    placeholder="z.B. Date Night, Tokyo Trip..."
                    value={newCollection.name}
                    onChange={(e) => setNewCollection(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-transparent border-border mt-1"
                    data-testid="collection-name-input"
                  />
                </div>
                
                <div>
                  <Label>Beschreibung (optional)</Label>
                  <Textarea
                    placeholder="Worum geht es in dieser Collection?"
                    value={newCollection.description}
                    onChange={(e) => setNewCollection(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-transparent border-border mt-1"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    {newCollection.is_public ? (
                      <Globe className="w-5 h-5 text-primary" />
                    ) : (
                      <Lock className="w-5 h-5 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium">{newCollection.is_public ? "Öffentlich" : "Privat"}</p>
                      <p className="text-xs text-muted-foreground">
                        {newCollection.is_public ? "Jeder mit dem Link kann sie sehen" : "Nur du und geteilte Freunde"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={newCollection.is_public}
                    onCheckedChange={(checked) => setNewCollection(prev => ({ ...prev, is_public: checked }))}
                  />
                </div>
                
                <Button 
                  onClick={createCollection} 
                  disabled={creating || !newCollection.name.trim()}
                  className="w-full bg-primary text-primary-foreground"
                  data-testid="create-collection-button"
                >
                  {creating ? "Erstelle..." : "Collection erstellen"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* My Collections */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Folder className="w-5 h-5 text-primary" />
            Meine Collections
            <span className="text-sm font-normal text-muted-foreground">({collections.length})</span>
          </h2>
          
          {collections.length === 0 ? (
            <Card className="bg-card border-border">
              <CardContent className="py-12 text-center">
                <Folder className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Noch keine Collections erstellt</p>
                <Button onClick={() => setShowNewDialog(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Erste Collection erstellen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {collections.map(collection => (
                <Card 
                  key={collection.collection_id}
                  className="bg-card border-border hover:border-primary/30 transition-all cursor-pointer group"
                  onClick={() => navigate(`/collections/${collection.collection_id}`)}
                  data-testid={`collection-card-${collection.collection_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{collection.emoji}</span>
                        <div>
                          <h3 className="font-semibold group-hover:text-primary transition-colors">
                            {collection.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {collection.restaurant_count} Restaurants
                          </p>
                        </div>
                      </div>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover border-border">
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCollection(collection);
                            setShareDialogOpen(true);
                          }}>
                            <Share2 className="w-4 h-4 mr-2" />
                            Teilen
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            copyShareLink(collection);
                          }}>
                            <Copy className="w-4 h-4 mr-2" />
                            Link kopieren
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-muted/50" />
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteCollection(collection.collection_id);
                            }}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {collection.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {collection.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {collection.is_public ? (
                        <Badge variant="outline" className="text-xs">
                          <Globe className="w-3 h-3 mr-1" />
                          Öffentlich
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          <Lock className="w-3 h-3 mr-1" />
                          Privat
                        </Badge>
                      )}
                      {collection.shared_with?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <User className="w-3 h-3 mr-1" />
                          {collection.shared_with.length} geteilt
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Shared With Me */}
        {sharedCollections.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Mit mir geteilt
              <span className="text-sm font-normal text-muted-foreground">({sharedCollections.length})</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedCollections.map(collection => (
                <Card 
                  key={collection.collection_id}
                  className="bg-card border-border hover:border-primary/30 transition-all cursor-pointer group"
                  onClick={() => navigate(`/collections/${collection.collection_id}`)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{collection.emoji}</span>
                        <div>
                          <h3 className="font-semibold group-hover:text-primary transition-colors">
                            {collection.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            von {collection.owner?.name}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground">
                      {collection.restaurant_count} Restaurants
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Collection teilen</DialogTitle>
          </DialogHeader>
          {selectedCollection && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center gap-3 p-4 bg-muted/20 rounded-lg">
                <span className="text-2xl">{selectedCollection.emoji}</span>
                <div>
                  <p className="font-medium">{selectedCollection.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCollection.restaurant_count} Restaurants</p>
                </div>
              </div>
              
              <div>
                <Label>Per Link teilen</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    readOnly
                    value={`${window.location.origin}/collections/shared/${selectedCollection.share_code}`}
                    className="bg-muted/20 border-border"
                  />
                  <Button onClick={() => copyShareLink(selectedCollection)} variant="outline">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div>
                <Label>Mit Freund teilen (E-Mail)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="freund@email.com"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="bg-transparent border-border"
                  />
                  <Button onClick={shareWithUser} disabled={!shareEmail.trim()}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Teilen
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Collections;
